import { getInitialState, gql } from '@/api/utils';
import { default as axios } from '@/api/axios';
import CityService from '@/api/services/CityService';

export const namespaced = true;

export const state = {
  loading: false,
  neverLoaded: true,
  data: null,
  error: null,
  logoURL: undefined,
  cityPhone: undefined,
  noLicensing: false,
  areaType: null,
  cityID: null,
  city: {},
  licensingCommunities: null,
  loadingCommunitiesError: false,
  licensingProvinces: [],
};

export const mutations = {
  SET_LOADING(state) {
    state.loading = true;
  },
  SET_LOADING_COMMUNITIES(state, loading) {
    state.loading = loading;
  },
  SET_LICENSING_COMMUNITY_ERROR(state, error) {
    state.loadingCommunitiesError = error;
  },
  SET_TOKEN(state, data) {
    if (!data.token) {
      state.token = data.token;
    }
  },
  UPDATE_LOGO_URL(state, data) {
    state.logoURL = data;
  },
  UPDATE_CITY_PHONE(state, data) {
    state.cityPhone = data;
  },
  IS_LICENSING_CITY(state, data) {
    state.noLicensing = data;
  },
  SET_AREA_TYPE(state, data) {
    state.areaType = data;
  },
  SET_CITY_ID(state, data) {
    state.cityID = data;
  },
  success(state, data) {
    state.neverLoaded = false;
    state.loading = false;
    state.data = data;
    state.error = null;
  },
  failure(state, error) {
    state.neverLoaded = false;
    state.loading = false;
    state.data = null;
    state.error = error;
  },
  clear(state) {
    const initialState = getInitialState();

    Object.keys(initialState).forEach((key) => {
      state[key] = initialState[key];
    });
  },
  SET_LICENSING_COMMUNITIES(state, communities) {
    state.licensingCommunities = communities;
  },
  CLEAR_LICENSING_COMMUNITIES(state) {
    state.licensingCommunities = [];
  },
  SET_LICENSING_PROVINCES(state, provinces) {
    state.licensingProvinces = provinces;
  },
};

export const actions = {
  async loadCity({ commit }) {
    commit('SET_LOADING');
    try {
      const query = gql`
        query userCity {
          userCity {
            id
            name
            title
            rabiesRequired
            displayRabiesField
            petDobRequired
            collectPetOwnerDob
            dogOnlyCity
            indoorCatsRabiesExempt
            addressValidation
            firstTimeLicensingOnly
            logo {
              original
            }
            customerService {
              phoneNumber
            }
            noLicensing
            areaType
            branding {
              customerSupportPhone
            }
            customerCareContext {
              customerCareSupportText {
                  color
                  text
              }
            }
          }
        }
      `;

      const { userCity: results } = await axios.post('/', {
        query,
      });

      if (results.logo) {
        commit('UPDATE_LOGO_URL', results.logo.original);
      }

      if (results.customerService) {
        commit('UPDATE_CITY_PHONE', results.customerService.phoneNumber);
      }

      commit('IS_LICENSING_CITY', results.noLicensing);
      commit('SET_AREA_TYPE', results.areaType);
      commit('SET_CITY_ID', results.id);
      commit('success', results);
    } catch (error) {
      commit('failure', error);
    }
  },
  async loadCityById({ commit }, cityId) {
    try {
      const query = gql`
        query userCity($id: ID!) {
          city(id: $id) {
            id
            logo {
              original
            }
            customerService {
              phoneNumber
            }
            noLicensing
            areaType
            firstTimeLicensingOnly
            customerCareContext {
              customerCareSupportText {
                  color
                  text
              }
            }
          }
        }
      `;

      const variables = {
        id: cityId,
      };

      const { city: results } = await axios.post('/', {
        query,
        variables,
      });

      if (results.logo) {
        commit('UPDATE_LOGO_URL', results.logo.original);
      }

      if (results.customerService) {
        commit('UPDATE_CITY_PHONE', results.customerService.phoneNumber);
      }

      commit('IS_LICENSING_CITY', results.noLicensing);
      commit('SET_AREA_TYPE', results.areaType);
      commit('success', results);
    } catch (error) {
      commit('failure', error);
    }
  },
  findLicensingCommunities({ commit }, input) {
    commit('SET_LOADING_COMMUNITIES', true);
    commit('SET_LICENSING_COMMUNITY_ERROR', false);
    CityService.getJurisdictionsByProvince(input)
      .then((response) => {
        commit('SET_LICENSING_COMMUNITIES', response);
      })
      .catch(() => {
        commit('SET_LICENSING_COMMUNITY_ERROR', true);
      })
      .finally(() => {
        commit('SET_LOADING_COMMUNITIES', false);
      });
  },
  findLicensingProvinces({ commit }) {
    commit('SET_LICENSING_COMMUNITY_ERROR', false);
    CityService.getLicensingProvinces()
      .then((response) => {
        commit('SET_LICENSING_PROVINCES', response);
      })
      .catch(() => {
        commit('SET_LICENSING_COMMUNITY_ERROR', true);
      })
      .finally(() => {});
  },
  clearLicensingCommunities({ commit }) {
    commit('CLEAR_LICENSING_COMMUNITIES');
  },
};

export const getters = {
  neverLoaded(state) {
    return state.neverLoaded;
  },
  loading(state) {
    return state.loading;
  },
  error(state) {
    return state.error;
  },
  loaded(state) {
    return !state.neverLoaded && !state.loading && (state.data || state.error);
  },
  success(state) {
    return !state.neverLoaded && !state.loading && state.data;
  },
  getLogoURL(state) {
    return state.logoURL || 'https://www.docupet.com/wp-content/uploads/2019/05/docupet-logo-sm-shelter.png';
  },
  getCityPhone(state) {
    return state.cityPhone || '1-855-249-1370';
  },
  noLicensing(state) {
    return state.noLicensing;
  },
  getAreaType(state) {
    return state.areaType;
  },
  getCityID(state) {
    return state.cityID;
  },
  getFirstTimeLicensingOnly(state) {
    return state.data?.firstTimeLicensingOnly;
  },
  getCustomerSupportPhone(state) {
    return state.data?.branding?.customerSupportPhone || '';
  },
  getCustomerCareText(state) {
    return state.data?.customerCareContext?.customerCareSupportText?.text || '';
  }
};
